import type React from 'react';

import { FocusRing } from '@change/ds-core-ui';
import { ArrowLeftIcon, ArrowRightIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';

type ButtonProps = { onClick: () => void; disabled: boolean };
type Props = {
	previous: ButtonProps;
	next: ButtonProps;
	prevFade?: string | string[];
	nextFade?: string | string[];
};

// The scroller spans every column, so cards sit under the gutters where these buttons live and the
// buttons have to out-stack them: z-20 clears the `relative z-10` @change/ds-forms puts on a
// *selected* SelectionCard, and the enabled state is `relative` (laid out identically to `static`)
// so that z-index applies at all.
const NAV_BUTTON_CLASS =
	'absolute z-20 row-start-1 flex h-9 w-9 place-self-center items-center justify-center rounded-full border border-stroke-weaker bg-background-base text-icon-strong elevation-low focus:relative disabled:cursor-default';

const FADE_CLASS = 'pointer-events-none absolute inset-y-0 w-[50px]';

function toBackground(fade: string | string[] | undefined): string | undefined {
	if (fade === undefined) return undefined;
	return Array.isArray(fade) ? fade[fade.length - 1] : fade;
}

export const NavigationButtonsV2: React.FC<Props> = ({ previous, next, prevFade, nextFade }) => {
	return (
		<div className="relative hidden md:contents">
			<FocusRing focusRingClass="rounded-full">
				<button
					type="button"
					onClick={previous.onClick}
					aria-disabled={previous.disabled}
					disabled={previous.disabled}
					data-testid="previous-button"
					className={cn(NAV_BUTTON_CLASS, 'col-start-1', previous.disabled ? 'md:absolute' : 'md:relative')}
					style={{ left: previous.disabled ? '-1000px' : '0' }}
				>
					<ArrowLeftIcon size={20} aria-hidden />
					<span className="sr-only">
						<Translate value="corgi.components.scroll_container.navigation_btn.previous" />
					</span>
				</button>
			</FocusRing>
			<FocusRing focusRingClass="rounded-full">
				<button
					type="button"
					onClick={next.onClick}
					aria-disabled={next.disabled}
					disabled={next.disabled}
					data-testid="next-button"
					className={cn(NAV_BUTTON_CLASS, 'col-start-3', next.disabled ? 'md:absolute' : 'md:relative')}
					style={{ right: next.disabled ? '-1000px' : '0' }}
				>
					<ArrowRightIcon size={20} aria-hidden />
					<span className="sr-only">
						<Translate value="corgi.components.scroll_container.navigation_btn.next" />
					</span>
				</button>
			</FocusRing>
			<div
				aria-hidden
				hidden={previous.disabled}
				className={cn(FADE_CLASS, 'left-0', previous.disabled ? 'hidden' : 'hidden md:block')}
				style={{ background: toBackground(prevFade) }}
			/>
			<div
				aria-hidden
				hidden={next.disabled}
				className={cn(FADE_CLASS, 'right-0', next.disabled ? 'hidden' : 'hidden md:block')}
				style={{ background: toBackground(nextFade) }}
			/>
		</div>
	);
};
